// The variants' API calls, all in one module (P4-B04 designer/router.py; docs/API.md «Variants»).
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/api/client';
import type { JobEvent } from '@/api/jobs';

export type VariantOut = Schemas['VariantOut'];
export type VariantKey = Schemas['VariantKey'];
export type VariantStatus = Schemas['VariantStatus'];
export type VariantState = Schemas['VariantState'];
export type RevisionOut = Schemas['RevisionOut'];
export type PreviewOut = Schemas['PreviewOut'];
export type GenerationStarted = Schemas['GenerationStarted'];

export const VARIANT_KEYS: VariantKey[] = ['A', 'B', 'C'];
export const RUNNING: ReadonlySet<VariantStatus> = new Set(['queued', 'generating', 'validating', 'building']);

export const variantsKey = (siteId: string) => ['variants', siteId] as const;

/** GET /admin/websites/{id}/variants: the latest round, A…C. Polls while one runs (the event stream also moves them). */
export const variantsQuery = (siteId: string) =>
  queryOptions({
    queryKey: variantsKey(siteId),
    queryFn: (): Promise<VariantOut[]> => unwrap(api.GET('/admin/websites/{website_id}/variants', { params: { path: { website_id: siteId } } })),
    refetchInterval: (q) => (q.state.data?.some((v) => RUNNING.has(v.status)) ? 3000 : false),
  });

/** POST /admin/websites/{id}/variants/generate (count 1–3, an optional prompt): the plan job, then one job per variant. */
export function generateVariants(siteId: string, count: number, prompt?: string | null): Promise<GenerationStarted> {
  return unwrap(api.POST('/admin/websites/{website_id}/variants/generate', { params: { path: { website_id: siteId } }, body: prompt ? { count, prompt } : { count } }));
}

/** «Повторить генерацию»: POST /admin/variants/{id}/retry (a failed variant only). */
export function retryVariant(variantId: string): Promise<VariantOut> {
  return unwrap(api.POST('/admin/variants/{variant_id}/retry', { params: { path: { variant_id: variantId } } }));
}

/** The colours the mini and stand-in previews paint with, from the variant's theme palette. */
export function landingPalette(v: Pick<VariantOut, 'palette'>): { bg: string; ink: string; acc: string } | null {
  const p = v.palette;
  if (!p?.background || !p.text) return null;
  return { bg: p.background, ink: p.text, acc: p.accent ?? p.primary ?? p.text };
}

/** The variant's preview screenshot at a width (the QA renderer's 1440 / 834 / 390). */
export function screenshot(v: Pick<VariantOut, 'preview'>, width: '1440' | '834' | '390'): string | null {
  return v.preview?.screenshots?.[width] ?? null;
}

/** A `job.variant` event's variant (docs/API.md «Variants»: the organization stream). */
type VariantEvent = Pick<VariantOut, 'id' | 'key' | 'round' | 'status' | 'progress' | 'error_code' | 'error_detail' | 'cost_usd'>;

/** Applies a `job.variant` event to the cached round; returns true when it touched a cached variant. */
export function applyVariantEvent(qc: QueryClient, siteId: string, e: JobEvent): boolean {
  if (e.type !== 'job.variant') return false;
  const ev = (e.data as { variant?: VariantEvent }).variant;
  if (!ev) return false;
  let hit = false;
  qc.setQueryData<VariantOut[]>(variantsKey(siteId), (list) =>
    list?.map((v) => {
      if (v.id !== ev.id) return v;
      hit = true;
      return { ...v, ...ev };
    }),
  );
  return hit;
}

/** Puts a move's answer (undo, redo, restore, checkpoint, inspector edit) into the cached variant. */
export function applyVariantState(qc: QueryClient, siteId: string, s: VariantState): void {
  qc.setQueryData<VariantOut[]>(variantsKey(siteId), (list) => list?.map((v) => (v.id === s.variant_id ? { ...v, head: s.head, can_undo: s.can_undo, can_redo: s.can_redo } : v)));
}
